import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../../components/layout/Navbar';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { useTheme } from '../../context/ThemeContext';
import authService from '../../api/services/authService';
import { handleApiError } from '../../utils/helpers';
import { USER_ROLES } from '../../utils/constants';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { success, error } = useToast();
  const { dark } = useTheme();
  const d = dark;

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.password) newErrors.password = 'Password is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      const response = await authService.login(formData);
      if (response.success) {
        // console.log('Login response:', response);
        login(response.data.user, response.data.token);
        success('Login successful!');
        let redirectTo = '/';
        if (response.data.user.role === USER_ROLES.SUPER_ADMIN) {
          redirectTo = '/admin/dashboard';
        } else if (response.data.user.role === USER_ROLES.COMPANY_ADMIN) {
          redirectTo = '/admin/hosts';
        } else if (response.data.user.role === USER_ROLES.SUB_ADMIN) {
          redirectTo = '/admin/hosts';
        } else if (response.data.user.role === USER_ROLES.VOTER) {
          redirectTo = '/';
        }
        const from = location.state?.from?.pathname || redirectTo;
        navigate(from, { replace: true });
      }
    } catch (err) {
      error(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap');
        .lp * { font-family: 'Plus Jakarta Sans', sans-serif !important; }

        @keyframes lp-up    { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        @keyframes lp-left  { from{opacity:0;transform:translateX(-24px)} to{opacity:1;transform:translateX(0)} }
        @keyframes lp-right { from{opacity:0;transform:translateX(24px)} to{opacity:1;transform:translateX(0)} }
        @keyframes lp-blob  {
          0%,100% { border-radius:60% 40% 30% 70% / 60% 30% 70% 40%; transform:scale(1) translate(0,0); }
          33%      { border-radius:30% 60% 70% 40% / 50% 60% 30% 60%; transform:scale(1.08) translate(10px,-10px); }
          66%      { border-radius:50% 60% 30% 60% / 40% 30% 60% 50%; transform:scale(.95) translate(-8px,8px); }
        }
        @keyframes lp-grad  { 0%,100%{background-position:0% 50%} 50%{background-position:100% 50%} }
        @keyframes lp-spin  { from{transform:rotate(0)} to{transform:rotate(360deg)} }
        @keyframes lp-pulse { 0%,100%{opacity:.5;transform:scale(1)} 50%{opacity:1;transform:scale(1.04)} }
        @keyframes lp-shine {
          0%   { transform:translateX(-100%) skewX(-15deg); }
          100% { transform:translateX(250%) skewX(-15deg); }
        }

        .lp .au  { animation: lp-up    .7s cubic-bezier(.22,1,.36,1) both; }
        .lp .al  { animation: lp-left  .7s cubic-bezier(.22,1,.36,1) both; }
        .lp .ar  { animation: lp-right .7s cubic-bezier(.22,1,.36,1) both; }

        .lp .d1  { animation-delay:.08s }
        .lp .d2  { animation-delay:.16s }
        .lp .d3  { animation-delay:.24s }
        .lp .d4  { animation-delay:.32s }
        .lp .d5  { animation-delay:.40s }
        .lp .d6  { animation-delay:.50s }

        .lp .blob {
          animation: lp-blob 9s ease-in-out infinite;
          position: absolute;
          filter: blur(56px);
        }
        .lp .blob-2 { animation-delay: -3s; }
        .lp .blob-3 { animation-delay: -6s; }

        .lp .grad-text {
          background: linear-gradient(135deg, #ef4444 0%, #f97316 40%, #ef4444 70%, #dc2626 100%);
          background-size: 300% 300%;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: lp-grad 4s ease infinite;
        }

        .lp .btn-submit {
          background: linear-gradient(135deg, #ef4444, #dc2626, #b91c1c);
          background-size: 200% 200%;
          animation: lp-grad 3s ease infinite;
          transition: transform .2s, box-shadow .2s;
          position: relative;
          overflow: hidden;
        }
        .lp .btn-submit:hover {
          transform: translateY(-2px) scale(1.02);
          box-shadow: 0 16px 40px rgba(239,68,68,.45);
        }
        .lp .btn-submit:active { transform: scale(.97); }
        .lp .btn-submit::after {
          content:'';
          position:absolute;
          top:0; left:0;
          width:35%; height:100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,.15), transparent);
          transform: translateX(-100%) skewX(-15deg);
        }
        .lp .btn-submit:hover::after {
          animation: lp-shine .5s ease forwards;
        }

        /* Input override for dark */
        .lp.dark-mode input {
          background: rgba(255,255,255,.05) !important;
          border-color: rgba(255,255,255,.1) !important;
          color: white !important;
        }
        .lp.dark-mode input:focus {
          border-color: #ef4444 !important;
          box-shadow: 0 0 0 3px rgba(239,68,68,.15) !important;
        }
        .lp.dark-mode input::placeholder { color: rgba(255,255,255,.25) !important; }
        .lp.dark-mode label { color: rgba(255,255,255,.7) !important; }

        /* Input override for light */
        .lp.light-mode input {
          background: white !important;
          border-color: #e5e7eb !important;
          color: #111827 !important;
        }
        .lp.light-mode input:focus {
          border-color: #ef4444 !important;
          box-shadow: 0 0 0 3px rgba(239,68,68,.1) !important;
        }
        .lp.light-mode label { color: #374151 !important; }

        .lp .feature-item {
          transition: all .3s cubic-bezier(.22,1,.36,1);
        }
        .lp .feature-item:hover {
          transform: translateX(6px);
        }

        .lp .spin-ring { animation: lp-spin 20s linear infinite; }
        .lp .spin-ring-rev { animation: lp-spin 15s linear infinite reverse; }
        .lp .pulse-card { animation: lp-pulse 3s ease-in-out infinite; }

        .lp .social-btn {
          transition: all .25s;
        }
        .lp .social-btn:hover {
          transform: translateY(-2px);
        }

        .lp .grid-bg-dark {
          background-image:
            linear-gradient(rgba(255,255,255,.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,.03) 1px, transparent 1px);
          background-size: 48px 48px;
        }
        .lp .grid-bg-light {
          background-image:
            linear-gradient(rgba(0,0,0,.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,0,0,.04) 1px, transparent 1px);
          background-size: 48px 48px;
        }
      `}</style>

      <Navbar />

      <div className={`lp ${d ? 'dark-mode' : 'light-mode'} min-h-screen flex pt-20 transition-colors duration-500 ${d ? 'bg-gray-950' : 'bg-gray-50'}`}>

        {/* ── LEFT PANEL ────────────────────────────────── */}
        <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">

          {/* BG */}
          <div className="absolute inset-0" style={{
            background: d
              ? 'linear-gradient(135deg, #030712 0%, #1a0000 40%, #030712 100%)'
              : 'linear-gradient(135deg, #fff1f2 0%, #ffe4e6 40%, #fff1f2 100%)',
          }}/>

          {/* Grid */}
          <div className={`absolute inset-0 ${d ? 'grid-bg-dark' : 'grid-bg-light'}`}/>

          {/* Blobs */}
          <div className="blob blob-1 w-80 h-80 top-16 left-10 opacity-30"
            style={{ background: d ? '#ef4444' : '#fca5a5' }}/>
          <div className="blob blob-2 w-72 h-72 top-48 right-8 opacity-20"
            style={{ background: d ? '#fff' : '#fed7aa' }}/>
          <div className="blob blob-3 w-64 h-64 bottom-20 left-32 opacity-25"
            style={{ background: d ? '#dc2626' : '#fca5a5' }}/>

          {/* Spinning decorative rings */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 opacity-10 spin-ring pointer-events-none">
            <svg viewBox="0 0 300 300" fill="none" className="w-full h-full">
              <circle cx="150" cy="150" r="140" stroke="#ef4444" strokeWidth="1.5" strokeDasharray="12 8"/>
            </svg>
          </div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 opacity-10 spin-ring-rev pointer-events-none">
            <svg viewBox="0 0 240 240" fill="none" className="w-full h-full">
              <circle cx="120" cy="120" r="110" stroke="#ef4444" strokeWidth="1" strokeDasharray="6 10"/>
            </svg>
          </div>

          {/* Content */}
          <div className="relative z-10 flex flex-col justify-center px-14 py-16">

            {/* Heading */}
            <div className="al d1 mb-3">
              <span className={`text-xs font-black tracking-widest uppercase px-3 py-1 rounded-full border ${d ? 'text-red-400 bg-red-500/10 border-red-500/20' : 'text-red-600 bg-red-50 border-red-200'}`}>
                Welcome Back
              </span>
            </div>
            <h1 className={`al d2 text-5xl font-black leading-tight mb-5 ${d ? 'text-white' : 'text-gray-900'}`}>
              Sign in to<br/>
              <span className="grad-text">eBallot Box</span>
            </h1>
            <p className={`al d3 text-base mb-12 leading-relaxed max-w-xs ${d ? 'text-gray-400' : 'text-gray-500'}`}>
              Continue managing your contests with live analytics, secure payments, and real-time voting.
            </p>

            {/* Features */}
            <div className="space-y-5 mb-12">
              {[
                { emoji:'🎯', title:'Manage Contests', desc:'Full control over your voting campaigns', delay:'d3' },
                { emoji:'📈', title:'Track Performance', desc:'Monitor votes and engagement in real-time', delay:'d4' },
                { emoji:'💼', title:'Business Tools', desc:'Professional features for organizations', delay:'d5' },
              ].map((f, i) => (
                <div key={i} className={`feature-item al ${f.delay} flex items-center gap-4`}>
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-xl flex-shrink-0 border ${d ? 'bg-white/8 border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}>
                    {f.emoji}
                  </div>
                  <div>
                    <p className={`text-sm font-bold ${d ? 'text-white' : 'text-gray-900'}`}>{f.title}</p>
                    <p className={`text-xs ${d ? 'text-gray-400' : 'text-gray-500'}`}>{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Trust badge */}
            <div className={`al d6 pulse-card rounded-2xl p-5 border ${d ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200 shadow-md'}`}>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-lg bg-green-500/15 flex items-center justify-center">
                  <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                  </svg>
                </div>
                <span className={`font-bold text-sm ${d ? 'text-white' : 'text-gray-900'}`}>Trusted Platform</span>
              </div>
              <p className={`text-xs leading-relaxed ${d ? 'text-gray-400' : 'text-gray-500'}`}>
                Join 500+ organizations using our secure voting platform
              </p>
            </div>
          </div>
        </div>

        {/* ── RIGHT PANEL ───────────────────────────────── */}
        <div className={`w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-10 lg:p-14 relative ${d ? 'bg-gray-950' : 'bg-white'}`}>

          {/* Subtle top glow */}
          <div className="absolute top-0 right-0 w-64 h-64 rounded-full blur-3xl opacity-10 pointer-events-none"
            style={{ background: 'radial-gradient(circle, #ef4444, transparent)' }}/>

          <div className="w-full max-w-md relative z-10">

            {/* Header */}
            <div className="au d1 text-center mb-8">
              <h2 className={`text-3xl font-black mb-1.5 ${d ? 'text-white' : 'text-gray-900'}`}>
                Sign In
              </h2>
              <p className={`text-sm ${d ? 'text-gray-400' : 'text-gray-500'}`}>
                Enter your credentials to continue
              </p>
            </div>

            {/* Form card */}
            <form onSubmit={handleSubmit} className="space-y-5">

              <div className={`au d2 rounded-2xl p-6 border transition-all duration-300 ${d
                ? 'bg-gray-900/80 border-white/8 hover:border-red-500/30'
                : 'bg-gray-50 border-gray-200 hover:border-red-300 shadow-sm'
              }`}>
                <div className="space-y-5">
                  <Input
                    label="Email Address"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    error={errors.email}
                    required
                  />

                  <Input
                    label="Password"
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    error={errors.password}
                    required
                  />

                  <div className="flex items-center justify-between pt-1">
                    <div className="flex items-center gap-2">
                      <input
                        id="remember"
                        name="remember"
                        type="checkbox"
                        className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-600 rounded cursor-pointer"
                        style={{ accentColor: '#ef4444' }}
                      />
                      <label htmlFor="remember" className={`text-sm cursor-pointer ${d ? 'text-gray-300' : 'text-gray-600'}`}>
                        Remember me
                      </label>
                    </div>
                    <Link to="/forgot-password" className="text-sm text-red-500 hover:text-red-400 font-semibold transition-colors">
                      Forgot password?
                    </Link>
                  </div>
                </div>
              </div>

              {/* Submit button */}
              <button
                type="submit"
                disabled={loading}
                className="au d3 btn-submit w-full text-white font-black text-base py-3.5 rounded-xl flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                    </svg>
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign In
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6"/>
                    </svg>
                  </>
                )}
              </button>

              {/* Divider */}
              <div className="au d4 relative flex items-center gap-4">
                <div className={`flex-1 h-px ${d ? 'bg-white/10' : 'bg-gray-200'}`}/>
                <span className={`text-xs font-bold uppercase tracking-wider ${d ? 'text-gray-500' : 'text-gray-400'}`}>or</span>
                <div className={`flex-1 h-px ${d ? 'bg-white/10' : 'bg-gray-200'}`}/>
              </div>

              {/* Social buttons */}
              <div className="au d5 grid grid-cols-2 gap-3">
                {[
                  {
                    label: 'Google',
                    icon: <path d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z"/>,
                  },
                  {
                    label: 'Facebook',
                    icon: <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>,
                  },
                ].map(({ label, icon }) => (
                  <button
                    key={label}
                    type="button"
                    className={`social-btn flex items-center justify-center gap-2.5 px-4 py-3 rounded-xl text-sm font-semibold border transition-colors ${d
                      ? 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10 hover:border-red-500/30'
                      : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-red-300 shadow-sm'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">{icon}</svg>
                    {label}
                  </button>
                ))}
              </div>

              {/* Register link */}
              <p className={`au d6 text-center text-sm ${d ? 'text-gray-400' : 'text-gray-500'}`}>
                Don't have an account?{' '}
                <Link to="/register" className="text-red-500 hover:text-red-400 font-bold transition-colors relative group inline-block">
                  Register here
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-red-500 group-hover:w-full transition-all duration-300"/>
                </Link>
              </p>

            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginPage;